import { useState } from "react";
import {
  ArrowLeftIcon,
  PlusIcon,
  SaveIcon,
  AlertTriangleIcon,
} from "lucide-react";
import { AcademicianButton } from "@/components/academician/academician-button";
import {
  ChapterEditorCard,
  ChapterData,
} from "@/components/academician/chapter-editor-card";
import { ContentItem } from "@/components/academician/content-picker-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface ModuleEditorPageProps {
  initialData: {
    id: number;
    title: string;
    description: string;
    recommendedWeek: number | null;
    status: string;
    chapters: Array<{
      id: number;
      title: string;
      position: number;
      items: Array<{
        id: number;
        itemType: string;
        itemId: number;
        title: string;
        duration?: string;
        questionsCount?: number;
        scheduledDate?: string;
        weekNumber?: number;
        position: number;
      }>;
    }>;
  };
  availableVideos: Array<{
    id: number;
    title: string;
    duration: string;
  }>;
  availableQuizzes: Array<{
    id: number;
    title: string;
    questionsCount: number;
  }>;
  availableBrainstormingSessions: Array<{
    id: number;
    title: string;
    scheduledDate: string;
    weekNumber: number;
    durationMinutes: number;
  }>;
  availableMeetupSessions: Array<{
    id: number;
    title: string;
    weekNumber: number;
    durationMinutes: number;
  }>;
  availableActivities: Array<{
    id: number;
    title: string;
    activityType: string;
    description: string;
  }>;
  availableDocuments: Array<{
    id: number;
    title: string;
    description: string;
    fileCount: number;
  }>;
  cohortsUsingModule: string[];
  csrfToken: string;
  updateStructureUrl: string;
  backUrl: string;
}

export function ModuleEditorPage({
  initialData,
  availableVideos,
  availableQuizzes,
  availableBrainstormingSessions,
  availableMeetupSessions,
  availableActivities,
  availableDocuments,
  cohortsUsingModule,
  csrfToken,
  updateStructureUrl,
  backUrl,
}: ModuleEditorPageProps) {
  const [title, setTitle] = useState(initialData.title);
  const [description, setDescription] = useState(initialData.description || "");
  const [recommendedWeek, setRecommendedWeek] = useState<string>(
    initialData.recommendedWeek?.toString() || ""
  );
  const [chapters, setChapters] = useState<ChapterData[]>(
    initialData.chapters
      .sort((a, b) => a.position - b.position)
      .map((ch) => ({
        id: ch.id,
        title: ch.title,
        items: ch.items
          .sort((a, b) => a.position - b.position)
          .map((item) => ({
            id: item.id,
            item_type: item.itemType as "Video" | "Quiz" | "BrainstormingSession" | "MeetupSession" | "Activity" | "Document",
            item_id: item.itemId,
            title: item.title,
            duration: item.duration,
            questionsCount: item.questionsCount,
            scheduledDate: item.scheduledDate,
            weekNumber: item.weekNumber,
          })),
      }))
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const videoItems: ContentItem[] = availableVideos.map((v) => ({
    id: v.id,
    title: v.title,
    type: "Video" as const,
    duration: v.duration,
  }));

  const quizItems: ContentItem[] = availableQuizzes.map((q) => ({
    id: q.id,
    title: q.title,
    type: "Quiz" as const,
    questionsCount: q.questionsCount,
  }));

  const brainstormingItems: ContentItem[] = availableBrainstormingSessions.map(
    (bs) => ({
      id: bs.id,
      title: bs.title,
      type: "BrainstormingSession" as const,
      scheduledDate: bs.scheduledDate,
      weekNumber: bs.weekNumber,
      durationMinutes: bs.durationMinutes,
    })
  );

  const meetupItems: ContentItem[] = availableMeetupSessions.map((ms) => ({
    id: ms.id,
    title: ms.title,
    type: "MeetupSession" as const,
    weekNumber: ms.weekNumber,
    durationMinutes: ms.durationMinutes,
  }));

  const activityItems: ContentItem[] = availableActivities.map((a) => ({
    id: a.id,
    title: a.title,
    type: "Activity" as const,
    activityType: a.activityType,
    description: a.description,
  }));

  const documentItems: ContentItem[] = availableDocuments.map((d) => ({
    id: d.id,
    title: d.title,
    type: "Document" as const,
    description: d.description,
    fileCount: d.fileCount,
  }));

  const handleAddChapter = () => {
    setChapters([...chapters, { title: "", items: [] }]);
  };

  const handleUpdateChapter = (index: number, updated: ChapterData) => {
    const newChapters = [...chapters];
    newChapters[index] = updated;
    setChapters(newChapters);
  };

  const handleDeleteChapter = (index: number) => {
    setChapters(chapters.filter((_, i) => i !== index));
  };

  const handleMoveChapterUp = (index: number) => {
    if (index === 0) return;
    const newChapters = [...chapters];
    [newChapters[index - 1], newChapters[index]] = [
      newChapters[index],
      newChapters[index - 1],
    ];
    setChapters(newChapters);
  };

  const handleMoveChapterDown = (index: number) => {
    if (index === chapters.length - 1) return;
    const newChapters = [...chapters];
    [newChapters[index], newChapters[index + 1]] = [
      newChapters[index + 1],
      newChapters[index],
    ];
    setChapters(newChapters);
  };

  const handleSave = async (status: "draft" | "published") => {
    if (!title.trim()) {
      setSaveMessage({ type: "error", text: "Module title is required." });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    const payload = {
      title,
      description,
      recommended_week: recommendedWeek ? parseInt(recommendedWeek, 10) : null,
      status,
      chapters: chapters.map((ch) => ({
        id: ch.id,
        title: ch.title,
        items: ch.items.map((item) => ({
          id: item.id,
          item_type: item.item_type,
          item_id: item.item_id,
        })),
      })),
    };

    try {
      const response = await fetch(updateStructureUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ module_structure: payload }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSaveMessage({
          type: "success",
          text:
            status === "published"
              ? "Module published successfully!"
              : "Module saved as draft.",
        });
        // Update local state with new IDs from server
        if (data.chapters) {
          setChapters(
            data.chapters.map(
              (ch: {
                id: number;
                title: string;
                items: Array<{
                  id: number;
                  itemType: string;
                  itemId: number;
                  title: string;
                  duration?: string;
                  questionsCount?: number;
                  scheduledDate?: string;
                  weekNumber?: number;
                  fileCount?: number;
                }>;
              }) => ({
                id: ch.id,
                title: ch.title,
                items: ch.items.map(
                  (item: {
                    id: number;
                    itemType: string;
                    itemId: number;
                    title: string;
                    duration?: string;
                    questionsCount?: number;
                    scheduledDate?: string;
                    weekNumber?: number;
                    fileCount?: number;
                  }) => ({
                    id: item.id,
                    item_type: item.itemType as "Video" | "Quiz" | "BrainstormingSession" | "MeetupSession" | "Activity" | "Document",
                    item_id: item.itemId,
                    title: item.title,
                    duration: item.duration,
                    questionsCount: item.questionsCount,
                    scheduledDate: item.scheduledDate,
                    weekNumber: item.weekNumber,
                    fileCount: item.fileCount,
                  })
                ),
              })
            )
          );
        }
      } else {
        setSaveMessage({
          type: "error",
          text: data.errors?.join(", ") || "Failed to save module.",
        });
      }
    } catch {
      setSaveMessage({
        type: "error",
        text: "Network error. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <a
            href={backUrl}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span className="text-sm">Back to Modules</span>
          </a>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Edit Module
              </h1>
              <p className="text-muted-foreground mt-1">
                Manage chapters and content for this module
              </p>
            </div>
            <div className="flex gap-3">
              <AcademicianButton
                variant="secondary"
                onClick={() => handleSave("draft")}
                loading={isSaving}
                disabled={isSaving}
              >
                Save as Draft
              </AcademicianButton>
              <AcademicianButton
                variant="primary"
                leftIcon={<SaveIcon />}
                onClick={() => handleSave("published")}
                loading={isSaving}
                disabled={isSaving}
              >
                Publish
              </AcademicianButton>
            </div>
          </div>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div
            className={`mb-6 p-4 rounded-lg border ${
              saveMessage.type === "success"
                ? "bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200"
                : "bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200"
            }`}
          >
            <p className="text-sm">{saveMessage.text}</p>
          </div>
        )}

        {/* Cohort Usage Warning */}
        {cohortsUsingModule.length > 0 && (
          <div className="mb-6 p-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950 dark:border-amber-800">
            <div className="flex items-start gap-3">
              <AlertTriangleIcon className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  This module is used in {cohortsUsingModule.length} cohort(s)
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  Changes will affect:{" "}
                  {cohortsUsingModule.join(", ")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Basic Info Card */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            Module Details
          </h2>
          <div className="space-y-4">
            <div>
              <Label
                htmlFor="module-title"
                className="text-sm font-medium text-foreground mb-2 block"
              >
                Title *
              </Label>
              <Input
                id="module-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Customer Discovery"
              />
            </div>
            <div>
              <Label
                htmlFor="module-description"
                className="text-sm font-medium text-foreground mb-2 block"
              >
                Description
              </Label>
              <Textarea
                id="module-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the module..."
                className="min-h-[80px] resize-none"
              />
            </div>
            <div className="max-w-xs">
              <Label
                htmlFor="module-week"
                className="text-sm font-medium text-foreground mb-2 block"
              >
                Recommended Week
              </Label>
              <Input
                id="module-week"
                type="number"
                min="1"
                value={recommendedWeek}
                onChange={(e) => setRecommendedWeek(e.target.value)}
                placeholder="e.g., 1"
              />
            </div>
          </div>
        </div>

        {/* Chapters Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Chapters ({chapters.length})
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Organize your module content into chapters
              </p>
            </div>
            <AcademicianButton
              variant="secondary"
              leftIcon={<PlusIcon />}
              onClick={handleAddChapter}
            >
              Add Chapter
            </AcademicianButton>
          </div>

          {chapters.length > 0 ? (
            <div className="space-y-4">
              {chapters.map((chapter, index) => (
                <ChapterEditorCard
                  key={chapter.id || `new-${index}`}
                  chapter={chapter}
                  index={index}
                  totalChapters={chapters.length}
                  onUpdate={(updated) => handleUpdateChapter(index, updated)}
                  onDelete={() => handleDeleteChapter(index)}
                  onMoveUp={() => handleMoveChapterUp(index)}
                  onMoveDown={() => handleMoveChapterDown(index)}
                  availableVideos={videoItems}
                  availableQuizzes={quizItems}
                  availableBrainstormingSessions={brainstormingItems}
                  availableMeetupSessions={meetupItems}
                  availableActivities={activityItems}
                  availableDocuments={documentItems}
                />
              ))}
            </div>
          ) : (
            <div className="bg-card border border-dashed border-border rounded-lg p-12 text-center">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <PlusIcon className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  No chapters yet
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Add chapters to organize your module content
                </p>
                <AcademicianButton
                  variant="primary"
                  leftIcon={<PlusIcon />}
                  onClick={handleAddChapter}
                >
                  Add Your First Chapter
                </AcademicianButton>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <a
            href={backUrl}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </a>
          <div className="flex gap-3">
            <AcademicianButton
              variant="secondary"
              onClick={() => handleSave("draft")}
              loading={isSaving}
              disabled={isSaving}
            >
              Save as Draft
            </AcademicianButton>
            <AcademicianButton
              variant="primary"
              leftIcon={<SaveIcon />}
              onClick={() => handleSave("published")}
              loading={isSaving}
              disabled={isSaving}
            >
              Publish
            </AcademicianButton>
          </div>
        </div>
      </div>
    </div>
  );
}
